import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Copy, Download, FileText, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";

type DocumentView = {
  key: string;
  title: string;
  sourceUrl: string;
  blobUrl: string;
};

export default function SourceDocumentPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const [params] = useSearchParams();
  const { currentUser, activeWorkspaceId } = useAuth();
  const workspaceId = params.get("workspaceId") || activeWorkspaceId;
  const key = `${currentUser?.uid}:${workspaceId}:${documentId}`;
  const [view, setView] = useState<DocumentView | null>(null);
  const [error, setError] = useState<{ key: string; message: string } | null>(
    null,
  );
  const [attempt, setAttempt] = useState(0);
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    if (!currentUser || !workspaceId || !documentId) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 60_000);
    let cancelled = false;
    let blobUrl: string | undefined;
    setView(null);
    setError(null);
    setCopyStatus("");
    const load = async () => {
      try {
        const token = await currentUser.getIdToken();
        const base = `${process.env.REACT_APP_API_BASE || "https://api.wavegen.ai"}/api/content/documents/${encodeURIComponent(documentId)}`;
        const options = {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-workspace-id": workspaceId,
          },
          signal: controller.signal,
          cache: "no-store" as const,
        };
        const metadata = await fetch(base, options);
        if (!metadata.ok)
          throw new Error(
            metadata.status === 401
              ? "Please sign in again to view this PDF."
              : "This PDF is unavailable, or you don't have access to its workspace.",
          );
        const { input } = await metadata.json();
        const response = await fetch(`${base}/file`, options);
        if (!response.ok)
          throw new Error(
            "This PDF is unavailable, or you don't have access to its workspace.",
          );
        const blob = await response.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setView({ key, title: input.title, sourceUrl: input.value, blobUrl });
      } catch (failure) {
        if (!cancelled)
          setError({
            key,
            message:
              failure instanceof Error && failure.name !== "AbortError"
                ? failure.message
                : "Loading this PDF took too long. Please try again.",
          });
      } finally {
        window.clearTimeout(timer);
      }
    };
    void load();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [currentUser, workspaceId, documentId, key, attempt]);

  // Never display a previous account/document while the next effect starts.
  const document = view?.key === key ? view : null;
  const failure = error?.key === key ? error.message : null;

  return (
    <main className="flex h-dvh flex-col bg-muted/30">
      <header className="flex flex-wrap items-center gap-4 border-b border-border bg-background px-5 py-4 sm:px-8">
        <Link
          to="/created"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Drafts
        </Link>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <FileText className="h-5 w-5 shrink-0 text-brand-ink" />
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold">
              {document?.title || "PDF document"}
            </h1>
            <p className="text-xs text-muted-foreground">Workspace document</p>
          </div>
        </div>
        {document && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(document.sourceUrl);
                  setCopyStatus(
                    "Link copied. Paste it into the source input to reuse this PDF.",
                  );
                } catch {
                  setCopyStatus(
                    "Couldn't copy the link. You can copy this page's address instead.",
                  );
                }
              }}
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy link
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href={document.blobUrl} download={document.title}>
                <Download className="mr-2 h-4 w-4" />
                Download
              </a>
            </Button>
          </div>
        )}
      </header>
      {copyStatus && document && (
        <p
          role="status"
          className="border-b border-border bg-background px-5 py-2 text-sm text-muted-foreground sm:px-8"
        >
          {copyStatus}
        </p>
      )}
      {failure ? (
        <div role="alert" className="m-auto max-w-md px-6 text-center">
          <FileText className="mx-auto mb-4 h-8 w-8 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Unable to open PDF</h2>
          <p className="my-3 text-sm text-muted-foreground">{failure}</p>
          <Button
            variant="outline"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </Button>
        </div>
      ) : document ? (
        <iframe
          title={document.title}
          src={document.blobUrl}
          className="min-h-0 w-full flex-1 border-0"
        />
      ) : (
        <div
          role="status"
          className="m-auto flex items-center gap-3 text-sm text-muted-foreground"
        >
          <Loader2 className="h-5 w-5 animate-spin" />
          Opening PDF…
        </div>
      )}
    </main>
  );
}
